import { useEffect, useState } from "react";
import { Send, Loader2, Mail, ChevronDown, RefreshCw, Trophy, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUSES = [
  { key: "sent", label: "Sent" },
  { key: "opened", label: "Opened" },
  { key: "called", label: "Called" },
  { key: "meeting_booked", label: "Meeting booked" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const STATUS_COLOUR = {
  sent: "bg-muted/40 text-muted-foreground",
  opened: "bg-primary/15 text-primary",
  called: "bg-primary/15 text-primary",
  meeting_booked: "bg-warning/15 text-warning",
  won: "bg-success/15 text-success",
  lost: "bg-destructive/15 text-destructive",
};


export default function PitchDeckSender() {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/pitch/log?limit=20")
      .then((r) => setLog(r.data.items || []))
      .catch(() => setLog([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="card-default p-5" data-testid="pitch-deck-sender">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="label-overline">Dealer outreach</div>
          </div>
          <div className="font-display font-bold text-xl tracking-tight mt-1">Send a pitch deck</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md leading-relaxed">
            Stamp the deck with the prospect's dealership name on slides 1 + 26, email it through your IONOS account, and track follow-up status here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-primary text-sm"
          data-testid="pitch-deck-open-modal-btn"
        >
          <Send className="w-4 h-4" /> New pitch
        </button>
      </div>

      {/* Recent log */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Recent pitches</div>
          <button onClick={refresh} className="text-muted-foreground hover:text-foreground" aria-label="Refresh" data-testid="pitch-log-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        {log.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6" data-testid="pitch-log-empty">
            No pitches sent yet. Click "New pitch" to send your first.
          </div>
        ) : (
          <ul className="divide-y divide-border text-sm" data-testid="pitch-log-list">
            {log.map((row) => <LogRow key={row.id} row={row} onChange={refresh} />)}
          </ul>
        )}
      </div>

      {open && <PitchModal onClose={() => setOpen(false)} onSent={refresh} />}
    </div>
  );
}


function LogRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/pitch/log/${row.id}`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      onChange();
    } catch { toast.error("Couldn't update status"); }
    finally { setBusy(false); }
  };

  return (
    <li className="py-3 flex items-center gap-3" data-testid={`pitch-log-row-${row.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{row.dealership_name}</div>
        <div className="text-[11px] text-muted-foreground truncate">
          {row.prospect_name} · {row.prospect_email} · {new Date(row.created_at).toLocaleDateString("en-GB")}
        </div>
      </div>
      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_COLOUR[row.status] || "bg-muted/40"}`}>
        {row.status.replace("_", " ")}
      </span>
      <details className="relative">
        <summary className="list-none cursor-pointer text-muted-foreground hover:text-foreground" data-testid={`pitch-log-status-${row.id}`}>
          <ChevronDown className="w-4 h-4" />
        </summary>
        <div className="absolute right-0 top-6 z-30 bg-card border border-border rounded-md shadow-xl py-1 w-40">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              type="button"
              disabled={busy || row.status === s.key}
              onClick={() => updateStatus(s.key)}
              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted/40 disabled:opacity-50"
              data-testid={`pitch-log-status-${row.id}-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </details>
    </li>
  );
}


function PitchModal({ onClose, onSent }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dealership, setDealership] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/pitch/prepare", {
        prospect_name: name.trim(),
        prospect_email: email.trim(),
        dealership_name: dealership.trim(),
        cover_note: note.trim() || undefined,
      });
      setDraft(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't build the deck");
    } finally { setBusy(false); }
  };

  const confirmSend = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await api.post(`/pitch/confirm/${draft.send_id}`);
      if (r.data.ok) {
        toast.success(`Pitch deck sent to ${name.trim()} at ${dealership.trim()}`);
        onSent();
        onClose();
      } else {
        toast.error(r.data.email_error || "Email send failed — check the log row for details");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the pitch");
    } finally { setBusy(false); }
  };

  const discard = async () => {
    if (draft) { try { await api.delete(`/pitch/draft/${draft.send_id}`); } catch {} }
    setDraft(null);
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && onClose()} data-testid="pitch-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-card rounded-lg border border-border shadow-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-overline text-primary">Personalised pitch deck</div>
            <h3 className="font-display font-bold text-lg mt-1">Send to a dealership</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="pitch-modal-close">
            <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-overline" htmlFor="pitch-name">Contact name</label>
            <input id="pitch-name" type="text" required value={name} onChange={(e) => setName(e.target.value)}
                   className="input-default" placeholder="Chris Patel" data-testid="pitch-input-name" />
          </div>
          <div>
            <label className="label-overline" htmlFor="pitch-email">Prospect email</label>
            <input id="pitch-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="input-default" placeholder="chris@dealership.co.uk" data-testid="pitch-input-email" />
          </div>
        </div>
        <div>
          <label className="label-overline" htmlFor="pitch-dealership">Dealership name</label>
          <input id="pitch-dealership" type="text" required value={dealership} onChange={(e) => setDealership(e.target.value)}
                 maxLength={120} className="input-default" placeholder="e.g. Toyota Service Centre Manchester"
                 data-testid="pitch-input-dealership" />
          <p className="text-[11px] text-muted-foreground mt-1">
            Appears on slide 1 ("Built for {`{dealership name}`}") and slide 26 ("{`{dealership name}`}, would you like to see it in action?")
          </p>
        </div>
        <div>
          <label className="label-overline" htmlFor="pitch-note">Cover note (optional)</label>
          <textarea id="pitch-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    maxLength={1200} className="input-default" placeholder="Great to meet you yesterday — as promised, here's the walkthrough…"
                    data-testid="pitch-input-note" />
          <p className="text-[11px] text-muted-foreground mt-1">{note.length}/1200 chars</p>
        </div>

        {draft && (
          <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2 text-xs" data-testid="pitch-modal-preview-panel">
            <div className="font-bold text-sm">Deck built — review before sending</div>
            <div className="text-muted-foreground">
              Cover: <span className="text-foreground font-semibold">"{draft.stamps?.cover}"</span><br />
              {draft.slides} slides · {draft.pictures} product screenshots embedded
            </div>
            {draft.pictures === 0 && (
              <div className="text-destructive font-bold">⚠ No screenshots embedded — do not send.</div>
            )}
            <a href={`/api/pitch/preview/${draft.send_id}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 underline text-primary" data-testid="pitch-modal-preview-download">
              Open the deck to check it
            </a>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          {!draft ? (
            <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="pitch-modal-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {busy ? "Building…" : "Build deck & preview"}
            </button>
          ) : (
            <>
              <button type="button" disabled={busy} onClick={confirmSend} className="btn-primary flex-1" data-testid="pitch-modal-confirm-send">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "Sending…" : "Looks good — send it"}
              </button>
              <button type="button" disabled={busy} onClick={discard} className="btn-outline" data-testid="pitch-modal-discard">
                Back
              </button>
            </>
          )}
          <button type="button" disabled={busy} onClick={onClose} className="btn-outline" data-testid="pitch-modal-cancel">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
